/**
 * What every access guard (AdminGuard, CapabilityGuard) needs settled before
 * it may evaluate: Central auth (the OIDC hand-off) and the workspace boot
 * (switch-context → capabilities).
 *
 * Staging defect: /admin mounted AdminGuard OUTSIDE BootGate. The guard waited
 * for `bootCompleted`, but the BootGate that performs the boot was its own
 * child — never rendered — so a direct load hung on "Loading..." forever (and
 * an unauthenticated direct load never reached the login redirect, because
 * the wait came first).
 *
 * The route tree now boots every guarded route (AuthGuard → BootGate → guard).
 * This hook additionally makes a guard independent of its placement: it never
 * waits on a boot it cannot see happening — when boot has not completed it
 * renders BootGate itself, which performs the boot (or shows the
 * Central-unavailable / workspace-closed overlay) and re-renders the guard
 * when done.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectIsCentralAuthenticated,
  selectIsCentralInitialized,
  selectIsSwitchingWorkspace,
} from '../../store/slices/centralAuthSlice';
import { selectBootCompleted } from '../../store/slices/bootContextSlice';
import { BootGate } from './BootGate';

export function RouteLoading({ label = 'Loading...' }: { label?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="route-loading">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
        <p className="mt-4 text-gray-500">{label}</p>
      </div>
    </div>
  );
}

/**
 * null when auth and boot are settled and the guard may evaluate; otherwise
 * the element the guard must render meanwhile (a spinner while auth state is
 * read, the login redirect, or BootGate driving the boot).
 */
export function useGuardPrerequisites(): React.ReactElement | null {
  const isInitialized = useSelector(selectIsCentralInitialized);
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const isSwitching = useSelector(selectIsSwitchingWorkspace);
  const bootCompleted = useSelector(selectBootCompleted);
  const location = useLocation();

  if (!isInitialized) return <RouteLoading />;
  // Same rule as AuthGuard: never redirect mid workspace switch.
  if (!isAuthenticated && !isSwitching) return <Navigate to="/login" state={{ from: location }} replace />;
  // BootGate performs the boot (or shows its overlay); its store update re-renders the guard.
  if (!bootCompleted || isSwitching) return <BootGate>{null}</BootGate>;
  return null;
}
